import { useQuery, gql, useMutation, QueryHookOptions } from '@apollo/client';
import {
  GQLMutationSymbolCollection_UpdateSymbolCollectionArgs,
  GQLQuerySymbolCollection_GetSymbolCollectionArgs,
  GQLQuerySymbolCollection_GetSymbolCollectionsByProducerArgs,
  GQLSymbolCollectionConnection,
  GQLQuerySymbolCollection_GetSymbolCollectionsByMerchantArgs,
  GQLQuerySymbolCollection_GetSymbolCollectionsByPlatformArgs,
  GQLSymbolCollection,
  GQLMutationSymbolCollection_CreateSymbolCollectionArgs,
  GQLQuerySymbolCollection_GetSymbolCollectionsArgs
} from '@jewelutions/core-crm-server-api-client-domain';
import { SYMBOL_COLLECTION_FIELDS } from './fragments/symbol';
import { OperationsHelper } from './OperationHelper';

export class SymbolCollectionOperation {
  symbolCollectionArrayFromResponse(data: any): GQLSymbolCollection[] {
    if (data.SymbolCollection_getSymbolCollections !== undefined) {
      return data.SymbolCollection_getSymbolCollections;
    }

    return OperationsHelper.retrievePropertyFromGQLResponse(data, 'symbolCollections');
  }

  symbolCollectionFromResponse(data: any): GQLSymbolCollection {
    return data.SymbolCollection_getSymbolCollection;
  }

  createSymbolCollection() {
    const mutation = gql`
      ${SYMBOL_COLLECTION_FIELDS}
      mutation createSymbolCollection($input: SymbolCollectionInput!) {
        SymbolCollection_createSymbolCollection(input: $input) {
          ...SymbolCollectionFields
        }
      }
    `;
    return useMutation<GQLSymbolCollection, GQLMutationSymbolCollection_CreateSymbolCollectionArgs>(mutation);
  }

  updateSymbolCollection() {
    const mutation = gql`
      ${SYMBOL_COLLECTION_FIELDS}
      mutation updateSymbolCollection($input: SymbolCollectionInput!, $symbolCollectionId: ID!) {
        SymbolCollection_updateSymbolCollection(input: $input, symbolCollectionId: $symbolCollectionId) {
          ...SymbolCollectionFields
        }
      }
    `;
    return useMutation<GQLSymbolCollection, GQLMutationSymbolCollection_UpdateSymbolCollectionArgs>(mutation);
  }

  getSymbolCollections(
    args: GQLQuerySymbolCollection_GetSymbolCollectionsArgs,
    options: QueryHookOptions<GQLSymbolCollection[], GQLQuerySymbolCollection_GetSymbolCollectionsArgs> = {}
  ) {
    const query = gql`
      ${SYMBOL_COLLECTION_FIELDS}
      query getSymbolCollections($symbolCollectionIds: [ID!]!) {
        SymbolCollection_getSymbolCollections(symbolCollectionIds: $symbolCollectionIds) {
          ...SymbolCollectionFields
        }
      }
    `;
    return useQuery<GQLSymbolCollection[], GQLQuerySymbolCollection_GetSymbolCollectionsArgs>(query, {
      ...options,
      variables: args
    });
  }

  getSymbolCollection(
    args: GQLQuerySymbolCollection_GetSymbolCollectionArgs,
    options: QueryHookOptions<GQLSymbolCollection, GQLQuerySymbolCollection_GetSymbolCollectionArgs> = {}
  ) {
    const query = gql`
      ${SYMBOL_COLLECTION_FIELDS}
      query getSymbolCollection($symbolCollectionId: ID!) {
        SymbolCollection_getSymbolCollection(symbolCollectionId: $symbolCollectionId) {
          ...SymbolCollectionFields
        }
      }
    `;
    return useQuery<GQLSymbolCollection, GQLQuerySymbolCollection_GetSymbolCollectionArgs>(query, {
      ...options,
      variables: args
    });
  }

  getSymbolCollectionsByPlatform(
    options: QueryHookOptions<
      GQLSymbolCollectionConnection,
      GQLQuerySymbolCollection_GetSymbolCollectionsByPlatformArgs
    > = {}
  ) {
    const query = gql`
      ${SYMBOL_COLLECTION_FIELDS}
      query getSymbolCollectionsByPlatform($pageSize: Int = 10) {
        SymbolCollection_getSymbolCollectionsByPlatform(pageSize: $pageSize) {
          symbolCollections {
            ...SymbolCollectionFields
          }
        }
      }
    `;
    return useQuery<
      GQLSymbolCollectionConnection,
      GQLQuerySymbolCollection_GetSymbolCollectionsByPlatformArgs
    >(query, {
      ...options
    });
  }

  getSymbolCollectionsByProducer(
    args: GQLQuerySymbolCollection_GetSymbolCollectionsByProducerArgs,
    options: QueryHookOptions<
      GQLSymbolCollectionConnection,
      GQLQuerySymbolCollection_GetSymbolCollectionsByProducerArgs
    > = {}
  ) {
    const query = gql`
      ${SYMBOL_COLLECTION_FIELDS}
      query getSymbolCollectionsByProducer($producerPartyId: ID!) {
        SymbolCollection_getSymbolCollectionsByProducer(producerPartyId: $producerPartyId, pageSize: 10) {
          symbolCollections {
            ...SymbolCollectionFields
          }
        }
      }
    `;
    return useQuery<
      GQLSymbolCollectionConnection,
      GQLQuerySymbolCollection_GetSymbolCollectionsByProducerArgs
    >(query, {
      ...options,
      variables: args
    });
  }

  getSymbolCollectionsByMerchant(
    args: GQLQuerySymbolCollection_GetSymbolCollectionsByMerchantArgs,
    options: QueryHookOptions<
      GQLSymbolCollectionConnection,
      GQLQuerySymbolCollection_GetSymbolCollectionsByMerchantArgs
    > = {}
  ) {
    const query = gql`
      ${SYMBOL_COLLECTION_FIELDS}
      query getSymbolCollectionsByMerchant($merchantPartyId: ID!, $pageSize: Int = 10) {
        SymbolCollection_getSymbolCollectionsByMerchant(
          merchantPartyId: $merchantPartyId
          pageSize: $pageSize
        ) {
          cursor
          hasMore
          symbolCollections {
            ...SymbolCollectionFields
          }
        }
      }
    `;
    return useQuery<
      GQLSymbolCollectionConnection,
      GQLQuerySymbolCollection_GetSymbolCollectionsByMerchantArgs
    >(query, {
      ...options,
      variables: args
    });
  }
}
